import {
  ExecutorCheckResult,
  LatencyCheckDTO,
  LatencyCheckResult,
  MonitoringExecutor,
} from '@meshwatch/types';

import { DynamoDBClient, PostgresClient } from '../../shared';
import { DEFAULT_DYNAMO_DB_CLIENT } from '../../shared/aws/dynamodb';
import { DEFAULT_POSTGRES_CLIENT } from '../../shared/postgres';
import { ForbiddenException, Pagination, UnreachableCaseError } from '../../shared/rest';
import BaseService, { ServiceResponse } from '../../shared/services';
import Authorization from '../../shared/services/access';
import { FormErrorsBoom } from '../../shared/validation/types';
import { DynamoMonitorDatasource, MonitorDatasource } from '../monitors/db';
import { isLatencyCheckMonitor } from '../monitors/validation';

import { LatencyCheckDatasource, TimescaleLatencyCheckDatasource } from './latency/db';
import { CHECKS_SCHEMA } from './schema';
import { IdentifiableMonitorExecutorCheck } from './types';

export class MonitorChecksDatasource {
  private readonly latency: LatencyCheckDatasource;
  private readonly client: PostgresClient;

  public constructor(client: PostgresClient = DEFAULT_POSTGRES_CLIENT) {
    this.latency = new TimescaleLatencyCheckDatasource(client);
    this.client = client;
  }

  public createChecksSchema = () => {
    return this.client.query({
      name: 'create-schema',
      text: `CREATE SCHEMA ${CHECKS_SCHEMA};`,
    });
  };

  public insertCheckData = <T extends ExecutorCheckResult>(
    data: IdentifiableMonitorExecutorCheck<T>,
    executorType: MonitoringExecutor
  ): Promise<LatencyCheckDTO> => {
    switch (executorType) {
      case 'latency-check':
        const latencyCheckData = data as IdentifiableMonitorExecutorCheck<LatencyCheckResult>;
        return this.latency.insertCheckData(latencyCheckData);
      case 'certificate-check':
        throw new Error('Not implemented');
      case 'https-check':
        throw new Error('Not implemented');
      default:
        throw new UnreachableCaseError(executorType);
    }
  };

  public getLatency = () => {
    return this.latency;
  };
}

export const monitorChecksDatasource = new MonitorChecksDatasource();

export class MonitorChecksService extends BaseService {
  private readonly latencyCheckDatasource: LatencyCheckDatasource;
  private readonly monitorDatasource: MonitorDatasource;

  public constructor(
    postgresClient: PostgresClient = DEFAULT_POSTGRES_CLIENT,
    dynamoClient: DynamoDBClient = DEFAULT_DYNAMO_DB_CLIENT
  ) {
    super();
    const monitorChecksDatasource = new MonitorChecksDatasource(postgresClient);
    this.latencyCheckDatasource = monitorChecksDatasource.getLatency();
    this.monitorDatasource = new DynamoMonitorDatasource(dynamoClient);
  }

  public getChecks = (
    userId: string,
    monitorId: string,
    pagination: Pagination = { limit: 20 }
  ): Promise<ServiceResponse<LatencyCheckDTO[] | FormErrorsBoom<undefined>>> => {
    return this.tryExecute(async () => {
      const monitor = await this.monitorDatasource.getMonitor(userId, monitorId);
      if (Authorization.of(userId).cannotAccessEntity(monitor)) {
        throw new ForbiddenException('You are not authorized to perform this action');
      }

      if (isLatencyCheckMonitor(monitor)) {
        return this.latencyCheckDatasource
          .getLatencyChecks({ monitorId, pagination })
          .then(this.serviceResponse);
      }

      throw new Error('Not implemented');
    });
  };
}

const monitorChecksService = new MonitorChecksService();

export default monitorChecksService;
